import { create } from 'zustand';

import {
  DESKTOP_MEDIA_QUERY,
  MD_MEDIA_QUERY,
  SM_MEDIA_QUERY,
} from '@/shared/configs/toolbar.config';

type BreakpointState = {
  isSm: boolean;
  isMd: boolean;
  isDesktop: boolean;
};

const matches = (query: string): boolean =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(query).matches
    : false;

/**
 * Single source of truth for the toolbar's breakpoint state. One matchMedia
 * listener per query is registered once for the whole app (below) rather than
 * each Toolbar/StickyContainer instance subscribing independently, so crossing
 * a breakpoint fires three handlers total instead of one per consumer.
 */
export const breakpointStore = create<BreakpointState>(() => ({
  isSm: matches(SM_MEDIA_QUERY),
  isMd: matches(MD_MEDIA_QUERY),
  isDesktop: matches(DESKTOP_MEDIA_QUERY),
}));

if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
  const bind = (query: string, key: keyof BreakpointState) => {
    const mql = window.matchMedia(query);
    const sync = () => breakpointStore.setState({ [key]: mql.matches } as Partial<BreakpointState>);
    sync();
    // Deprecated add/removeListener kept for Safari < 14 (see media.hooks).
    if (mql.addEventListener) {
      mql.addEventListener('change', sync);
    } else {
      mql.addListener(sync);
    }
  };

  bind(SM_MEDIA_QUERY, 'isSm');
  bind(MD_MEDIA_QUERY, 'isMd');
  bind(DESKTOP_MEDIA_QUERY, 'isDesktop');
}
